import { Autocomplete, Checkbox, FormControlLabel, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import { ProposalMeasurementType } from "generated/graphql";
import { ProductOption } from "../measurementTypes";
import { isMeasurementCompatible, StageMeasurementDraft } from "../stageMeasurements";
import { useMemo } from "react";

type MeasurementCardProps = {
  measurement: StageMeasurementDraft;
  isSelected: boolean;
  products: ProductOption[];
  onSelect: (featureId: string) => void;
  onUpdateProduct: (featureId: string, product: ProductOption | null) => void;
  onToggleScreenshot: (featureId: string, checked: boolean) => void;
};

export default function MeasurementCard({
  measurement,
  isSelected,
  products,
  onSelect,
  onUpdateProduct,
  onToggleScreenshot,
}: MeasurementCardProps) {
  const compatibleProducts = useMemo(
    () =>
      products
        .filter((product) =>
          isMeasurementCompatible(measurement.measurementType, product.measurementJob)
        )
        .sort((a, b) => {
          const catA = a.organizationProductType?.name ?? "";
          const catB = b.organizationProductType?.name ?? "";
          if (catA !== catB) return catA.localeCompare(catB);
          return (a.name ?? "").localeCompare(b.name ?? "");
        }),
    [products, measurement.measurementType]
  );
  const selectedProduct = products.find((product) => product.id === measurement.productId) ?? null;
  const metricsLabel =
    measurement.measurementType === ProposalMeasurementType.AREA
      ? `${measurement.metrics.areaFeet.toLocaleString()} ft² / ${measurement.metrics.areaYards.toLocaleString()} yd²`
      : `${measurement.metrics.perimeterFeet.toLocaleString()} ft / ${measurement.metrics.perimeterYards.toLocaleString()} yd`;

  return (
    <MDBox
      onClick={() => onSelect(measurement.featureId)}
      sx={{
        border: `1px solid ${isSelected ? "#0094c6" : "#E5E7EB"}`,
        borderRadius: "10px",
        padding: "10px 12px 4px",
        cursor: "pointer",
        background: isSelected ? "rgba(0, 148, 198, 0.06)" : "#fff",
      }}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <UiTypography uiVariant="body-sm" sx={{ fontWeight: 600 }}>
          {measurement.label}
        </UiTypography>
        {measurement.color && (
          <MDBox
            sx={{
              width: 16,
              height: 16,
              borderRadius: "4px",
              backgroundColor: measurement.color,
              border: "1px solid rgba(0,0,0,0.15)",
              flexShrink: 0,
            }}
          />
        )}
      </MDBox>
      <UiTypography uiVariant="caption" sx={{ color: "#667085" }}>
        {metricsLabel}
      </UiTypography>
      <Autocomplete
        fullWidth
        size="small"
        options={compatibleProducts}
        groupBy={(option) => option.organizationProductType?.name ?? ""}
        getOptionLabel={(option) => option.name ?? ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedProduct}
        onClick={(event) => event.stopPropagation()}
        onChange={(event, nextProduct) => {
          event.stopPropagation();
          onUpdateProduct(measurement.featureId, nextProduct);
        }}
        componentsProps={{ popper: { style: { zIndex: 1600 } } }}
        sx={{ mt: 1.5 }}
        renderInput={(params) => <TextField {...params} placeholder="Assign Product" />}
      />
      <FormControlLabel
        sx={{
          mt: 1,
          ml: 0,
          "& .MuiFormControlLabel-label": { fontSize: 13 },
          "& .MuiCheckbox-root": { pl: 0 },
        }}
        onClick={(event) => event.stopPropagation()}
        control={
          <Checkbox
            size="small"
            checked={!!measurement.isScreenshotTarget}
            disabled={!measurement.productId}
            onChange={(event) => onToggleScreenshot(measurement.featureId, event.target.checked)}
          />
        }
        label="Add screenshot"
      />
    </MDBox>
  );
}
